import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Calendar, Views, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import "moment-timezone";
import { ChevronLeft, ChevronRight, User, CalendarDays } from "lucide-react";
import { ReactComponent as CalendarViewIcon } from "../../Assets/Images/calendarviewicon.svg";
import { ReactComponent as DetailedViewIcon } from "../../Assets/Images/detailedviewicon.svg";
import CandidateInterviewDetails from "./CandidateInterviewDetails.js";
import VybogTALLoaderAnimation from "../Dynamic-Animations/DynamicAnimations.js";
import "../../Components/Applicant-Tracking/Candidates/Candidates.css";
import "./CandidateInterviewDetails.css";
import "./CalendarScheduler.css";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import Button, { StyledButton } from "../Button/Button.js";
import calendarIcon from "../../Assets/Images/calendarIcon.png";
import darkCalendarIcon from "../../Assets/Images/calendar-dark-Icon.png";
import { DynamicContext } from "../../Context/DynamicContext.js";
import TimezoneSelector from "./TimezoneSelector.js";
import SidePopup from "../../Common/SidePopup/SidePopup.js";
import Textfield from "../../Common/Textfield/Textfield.js";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput.jsx";
import * as Yup from "yup";
import * as StyleDOM from "../../Components/Applicant-Tracking/Candidates/CandidateDetails/CandidateDocuments.jsx";
import CustomTooltip from "../../Common/Tooltip/Tooltip.js";
import filterInfoIcon from "../../Assets/Images/infoicon.svg";
import { useFormik } from "formik";
import candidateIcon from "../../Assets/Images/candidate-schedule-icon.png";
import emailIcon from "../../Assets/Images/email-schedule-icon.png";
import contactNoIcon from "../../Assets/Images/phone-schedule-icon.png";
import jobIcon from "../../Assets/Images/job-schedule-icon.png";
import interviewPersonIcon from "../../Assets/Images/interview-person-schedule-icon.png";
import dateIcon from "../../Assets/Images/calendar-schedule-icon.png";
import linkIcon from "../../Assets/Images/link-schedule-icon.png";
import rescheduleIcon from "../../Assets/Images/reschedule-interview-icon.png";
import cancelScheduleIcon from "../../Assets/Images/cancel-interview-icon.png";
import ContentDialog from "../Content-Dialog/ContentDialog.js";
import {
  applyEllipsis,
  formatInterviewerName,
} from "../Common-Function/CommonFunctions.js";
import { post, get, deleteRequest, put } from "../../helper/Api_helper";
import Accordion from "../Accordion/Accordion.js";
import SelectInput from "../../Common/Select-Input/SelectInput";
import {
  VYLABS_BASE_URL,
  SCHEDULE_INTERVIEW,
  DELETE_INTERVIEW,
  UPDATE_INTERVIEW,
  GET_INTERVIEWS_BY_ID,
  INTERVIEW_EMAIL_PREVIEW,
  INTERVIEW_BY_CANDIDATE,
  JOBS_BY_CANDIDATE,
  INTERVIEW_PANEL_SET,
  INTERVIEW_PANEL_FEEDBACK,
} from "../../helper/url_helper";
import EmailPreviewPopup from "../EmailPreviewPopup/EmailPreviewPopup";
import ReactDOM from "react-dom";
import { CustomDatePickerModal } from "../Date-Picker/DatePicker";
import CustomTimePicker from "../Time-Picker/TimePicker";
import { convertUTCTimeToUserTimezone } from "../Time-Zone-Utils/timezoneUtils.js";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { filterPayload } from "../Common-Function/CommonFunctions.js";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { StandardPopup } from "../PopupStyles/PopupComponent.js";
import CandidateInterviewPanel from "./CandidateInterviewPanel.js";
import SelectedStarIcon from "../../Assets/Images/SelectedStar.svg";
import NotSelectedStarIcon from "../../Assets/Images/NotSelectedStar.svg";
import TextArea from "../TextArea/TextArea.js";
import MonthFilter from "../Month-Filter/MonthFilter.js";

const normalizeInterviewValues = (
  values,
  interviewType = values.interviewType,
) => ({
  ...values,
  interviewType,
  ...(interviewType === "online"
    ? {
        address1: "",
        address2: "",
        country: "",
        state: "",
        city: "",
        zipcode: "",
      }
    : { meetingLink: "" }),
});

const CandidateInterviewScheduler = ({
  initialEvents,
  candidateDetails,
  applicationId,
  hasApplicationIds,
  headerClassName = "calendar-header",
}) => {
  const candidateId = candidateDetails?.candidate?.id || candidateDetails?.id;
  const [events, setEvents] = useState(
    initialEvents?.map((event) => ({
      ...event,
      id: event.id || crypto.randomUUID(),
      start: new Date(event.start),
      end: new Date(event.end),
    })) || [],
  );
  const [candidate, setCandidate] = useState(candidateDetails);
  const {
    isDarkMode,
    userDetails,
    userTimezone,
    isMonthlyEmailLimitReached,
    getMonthlyEmailTooltip,
    isHiringTeamTenant,
  } = useContext(DynamicContext);
  const [currentTimezone, setCurrentTimezone] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.DAY);
  const [displayView, setDisplayView] = useState("detailed");
  const [interviewDetails, setInterviewDetails] = useState({});
  const [isInterviewsLoading, setIsInterviewsLoading] = useState(
    Boolean(candidateId),
  );
  const [loadedCandidateId, setLoadedCandidateId] = useState(null);
  const [interviewsError, setInterviewsError] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [detailsRetryKey, setDetailsRetryKey] = useState(0);
  const schedulerContentId = React.useId();
  const interviewsRequestRef = useRef(0);
  const isListLoading =
    isInterviewsLoading ||
    Boolean(candidateId && loadedCandidateId !== candidateId);
  const isDetailsLoading =
    displayView === "detailed" &&
    !detailsError &&
    events.some(
      (event) =>
        event.id &&
        !Object.prototype.hasOwnProperty.call(interviewDetails, event.id),
    );
  const isSchedulerLoading =
    isListLoading || (!interviewsError && isDetailsLoading);
  const schedulerError =
    interviewsError || (displayView === "detailed" ? detailsError : "");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [showCandidateDetails, setShowCandidateDetails] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isFeedbackPopupOpen, setIsFeedbackPopupOpen] = useState(false);
  const [feedbackMember, setFeedbackMember] = useState(null);
  const [hoveredFeedbackScore, setHoveredFeedbackScore] = useState(0);
  const [storePanelData, setStorePanelData] = useState([]);
  const [activeDatePicker, setActiveDatePicker] = useState(null);
  const [pickerPosition, setPickerPosition] = useState({
    top: null,
    left: null,
    placement: "bottom",
  });
  const calendarIconRef = useRef(null);
  const interviewDateRef = useRef(null);
  const pickerRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const [newEvent, setNewEvent] = useState({
    start: null,
    end: null,
    candidateName: candidate?.candidate?.name,
    position: candidate?.candidate?.role,
    background: "linear-gradient(rgb(158 153 235) 0%, rgb(66, 56, 224) 100%)",
    textColor: "#ffffff",
  });
  const [isDragging, setIsDragging] = useState(false);
  const [interviewsList, setInterviewsList] = useState([]);
  const [interviewID, setInterviewID] = useState("");
  const [selectedIid, setSelectedIid] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    body: "",
    subject: "",
    isLoading: false,
  });
  const [jobFilterOptions, setJobFilterOptions] = useState([]);
  const [selectedFilterJob, setSelectedFilterJob] = useState(null);
  const [panelMemberIds, setPanelMemberIds] = useState([]);
  const localizer = useMemo(() => {
    moment.tz.setDefault(currentTimezone);
    return momentLocalizer(moment);
  }, [currentTimezone]);
  useEffect(() => {
    setCandidate(candidateDetails);
  }, [candidateDetails]);
  useEffect(() => {
    if (
      userTimezone &&
      typeof userTimezone === "string" &&
      userTimezone.trim() !== ""
    ) {
      setCurrentTimezone(userTimezone.trim());
    }
  }, [userTimezone]);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validationSchema = Yup.object().shape({
    candidateName: Yup.string().required("Candidate name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    interviewPerson: Yup.string().required("Interview person is required"),
    interviewDate: Yup.date()
      .required("Interview date is required")
      .typeError("Please select a valid date")
      .min(moment().startOf("day").toDate(), "Date cannot be in the past"),
    startTime: Yup.string().required("Start time is required"),
    endTime: Yup.string()
      .required("End time is required")
      .test(
        "is-after-start",
        "End time must be after start time",
        function (endTime) {
          const { interviewDate, startTime } = this.parent;
          if (!interviewDate || !startTime || !endTime) return true;

          const start = new Date(
            `${moment(interviewDate).format("YYYY-MM-DD")} ${startTime}`,
          );
          const end = new Date(
            `${moment(interviewDate).format("YYYY-MM-DD")} ${endTime}`,
          );
          return end > start;
        },
      ),
    interviewType: Yup.string()
      .oneOf(["online", "offline"], "Please select interview type")
      .required("Interview type is required"),
    meetingLink: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "online",
      then: (schema) =>
        schema.required("Meeting link is required for online interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
    address1: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "offline",
      then: (schema) =>
        schema.required("Address is required for in-person interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
    country: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "offline",
      then: (schema) =>
        schema.required("Country is required for in-person interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
    state: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "offline",
      then: (schema) =>
        schema.required("State is required for in-person interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
    city: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "offline",
      then: (schema) =>
        schema.required("City is required for in-person interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
    zipcode: Yup.string().when(["interviewType"], {
      is: (interviewType) => interviewType === "offline",
      then: (schema) =>
        schema.required("Zipcode is required for in-person interviews"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const feedbackFormik = useFormik({
    initialValues: {
      score: 4,
      hiringRecommendation: "Hire",
      feedbackNotes: "",
    },
    onSubmit: async () => {
      await createInterviewPanelFeedback();
    },
  });
  const resetFeedbackForm = feedbackFormik.resetForm;

  useEffect(() => {
    if (isFeedbackPopupOpen) {
      resetFeedbackForm({
        values: {
          score: feedbackMember?.rating ?? 4,
          hiringRecommendation: feedbackMember?.hiring_recommendation || "Hire",
          feedbackNotes: feedbackMember?.notes ?? "",
        },
      });
      setHoveredFeedbackScore(0);
    }
  }, [isFeedbackPopupOpen, feedbackMember, resetFeedbackForm]);

  const scheduleformik = useFormik({
    initialValues: {
      candidateName: candidate?.candidate?.name || "",
      appliedJob: candidate?.candidate?.role || "",
      email: candidate?.candidate?.email || "",
      contactNo: candidate?.candidate?.phone || "",
      interviewPerson:
        `${userDetails?.first_name} ${userDetails?.last_name}` || "",
      interviewDate: interviewsList?.[0]?.date || null,
      startTime: interviewsList?.[0]?.time_start || "",
      endTime: interviewsList?.[0]?.time_end || "",
      address1: interviewsList?.[0]?.address || "",
      address2: interviewsList?.[0]?.address2 || "",
      country: interviewsList?.[0]?.country || "",
      state: interviewsList?.[0]?.state || "",
      city: interviewsList?.[0]?.city || "",
      zipcode: interviewsList?.[0]?.zip_code || "",
      meetingLink: interviewsList?.[0]?.meeting_link || "",
      timezone: currentTimezone,
      interviewType: interviewsList?.[0]?.meetingLink ? "online" : "offline",
      companyName: "",
      sendEmail: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (formValues) => {
      const values = normalizeInterviewValues(formValues);
      if (!values.interviewDate) {
        scheduleformik.setFieldError(
          "interviewDate",
          "Interview date is required",
        );
        return;
      }
      if (jobFilterOptions.length > 1 && !selectedFilterJob?.value) {
        Toast("error", "Please select a job from the filter.");
        return;
      }
      if (values.interviewType === "online" && !values.meetingLink) {
        Toast("error", "Meeting link is required for online interviews");
        return;
      }

      if (values.interviewType === "offline" && !values.address1) {
        Toast("error", "Address is required for in-person interviews");
        return;
      }

      const applicationID =
        selectedFilterJob?.value ||
        applicationId ||
        candidate?.application_ids?.[0];

      if (!applicationID) {
        Toast("error", "No valid application ID found for this candidate");
        return;
      }
      try {
        const rawPayload = {
          address: `${values.address1} ${values.address2}`.trim(),
          applicationID: applicationID,
          city: values.city,
          country: values.country,
          date: values.interviewDate
            ? `${values.interviewDate.getFullYear()}-${String(
                values.interviewDate.getMonth() + 1,
              ).padStart(2, "0")}-${String(
                values.interviewDate.getDate(),
              ).padStart(2, "0")}`
            : null,
          interviewerName: values.interviewPerson,
          meetingLink: values.meetingLink,
          state: values.state,
          timeEnd: values.endTime,
          timeStart: values.startTime,
          zipCode: values.zipcode?.toString() || "",
          timezone: scheduleformik.values.timezone,
          companyName: scheduleformik.values.companyName,
          send_email: values.sendEmail,
        };
        const payload = filterPayload(rawPayload);
        const scheduleResponse = await post(
          VYLABS_BASE_URL,
          SCHEDULE_INTERVIEW,
          payload,
        );
        Toast("success", "Interview Scheduled Successfully");
        const newInterviewId = scheduleResponse?.id;
        if (panelMemberIds.length > 0 && newInterviewId) {
          try {
            await put(VYLABS_BASE_URL, INTERVIEW_PANEL_SET, {
              interview_id: newInterviewId,
              members: panelMemberIds.map((id) => ({ user_id: id })),
            });
          } catch (panelError) {
            console.error(
              "Error setting interview panel:",
              panelError.response?.data || panelError.message,
            );
            Toast(
              "error",
              panelError.response?.data?.message ||
                "Interview scheduled, but failed to assign the interview panel",
            );
          }
        }

        scheduleformik.resetForm();
        setPanelMemberIds([]);
        handleCloseModal();
        await refetchInterviews();
      } catch (error) {
        console.error(
          "Error scheduling interview:",
          error.response?.data || error.message,
        );
        Toast(
          "error",
          error.response?.data?.message || "Failed to schedule interview",
        );
      }
    },
  });

  const handleInterviewTypeChange = (interviewType) => {
    scheduleformik.setValues((values) =>
      normalizeInterviewValues(values, interviewType),
    );
  };

  const updatingInterview = async () => {
    try {
      const values = normalizeInterviewValues(scheduleformik.values);
      const applicationID =
        interviewsList.find((interview) => interview.id === interviewID)
          ?.applicationId ||
        selectedFilterJob?.value ||
        applicationId ||
        candidate?.application_ids?.[0];

      if (!applicationID) {
        Toast("error", "No valid application ID found for this candidate");
        return;
      }
      if (values.interviewType === "online" && !values.meetingLink) {
        Toast("error", "Meeting link is required for online interviews");
        return;
      }

      if (values.interviewType === "offline" && !values.address1) {
        Toast("error", "Address is required for in-person interviews");
        return;
      }
      if (values.interviewType === "offline" && !values.state) {
        Toast("error", "State is required for in-person interviews");
        return;
      }
      if (values.interviewType === "offline" && !values.zipcode) {
        Toast("error", "Zipcode is required for in-person interviews");
        return;
      }

      const rawPayload = {
        id: interviewID,
        address: values.address1,
        address2: values.address2,
        applicationID: applicationID,
        city: values.city,
        country: values.country,
        date: values.interviewDate
          ? `${values.interviewDate.getFullYear()}-${String(
              values.interviewDate.getMonth() + 1,
            ).padStart(2, "0")}-${String(
              values.interviewDate.getDate(),
            ).padStart(2, "0")}`
          : null,
        interviewerName: values.interviewPerson,
        meetingLink: values.meetingLink,
        state: values.state,
        timeEnd: values.endTime,
        timeStart: values.startTime,
        zipCode: values.zipcode?.toString() || "",
        timezone: scheduleformik.values.timezone,
        companyName: scheduleformik.values.companyName,
        send_email: values.sendEmail,
      };
      // Preserve explicit empty strings after filtering to clear the previous mode.
      const payload = {
        ...filterPayload(rawPayload),
        ...(values.interviewType === "online"
          ? {
              address: "",
              address2: "",
              country: "",
              state: "",
              city: "",
              zipCode: "",
            }
          : { meetingLink: "", address2: values.address2 || "" }),
      };
      await put(VYLABS_BASE_URL, UPDATE_INTERVIEW, payload);
      if (isHiringTeamTenant) {
        await put(VYLABS_BASE_URL, INTERVIEW_PANEL_SET, {
          interview_id: interviewID,
          members: panelMemberIds.map((id) => ({ user_id: id })),
        });
      }
      Toast("success", "Interview Rescheduled Successfully");
      const newInterviewId = interviewID;
      if (newInterviewId) {
        try {
          await put(VYLABS_BASE_URL, INTERVIEW_PANEL_SET, {
            interview_id: newInterviewId,
            members: panelMemberIds.map((id) => ({ user_id: id })),
          });
        } catch (panelError) {
          console.error(
            "Error setting interview panel:",
            panelError.response?.data || panelError.message,
          );
          Toast(
            "error",
            panelError.response?.data?.message ||
              "Interview scheduled, but failed to assign the interview panel",
          );
        }
      }
      await refetchInterviews();
      setShowModal(false);
    } catch (error) {
      console.error(
        "Error rescheduling interview:",
        error.response?.data || error.message,
      );
      Toast(
        "error",
        error.response?.data?.message || "Failed to reschedule interview",
      );
    }
  };

  const handleRescheduleInterview = async (interviewToEdit) => {
    const selectedInterviewId = interviewToEdit?.id || selectedIid;

    if (!selectedInterviewId) {
      console.error("No selected interview ID");
      return;
    }

    const interview = interviewsList.find(
      (interview) => String(interview.id) === String(selectedInterviewId),
    );

    if (!interview) {
      console.error("No interview found with ID:", selectedInterviewId);
      return;
    }

    const interviewDetails =
      await fetchcandidatebyInterviewId(selectedInterviewId);
    const panelMembers = interviewDetails
      ? Array.isArray(interviewDetails.interview?.panel)
        ? interviewDetails.interview.panel
        : []
      : Array.isArray(storePanelData)
        ? storePanelData
        : [];

    const existingPanelIds = Array.from(
      new Map(
        panelMembers
          .map((member) => member?.user_id)
          .filter(
            (userId) =>
              userId !== undefined &&
              userId !== null &&
              String(userId).trim() !== "",
          )
          .map((userId) => [String(userId), userId]),
      ).values(),
    );
    setPanelMemberIds(existingPanelIds);

    const interviewTimezone = interview.timezone;

    setInterviewID(selectedInterviewId);
    const startTimeFormatted = moment(interview.start)
      .tz(interviewTimezone || "UTC")
      .format("HH:mm");
    const endTimeFormatted = moment(interview.end)
      .tz(interviewTimezone || "UTC")
      .format("HH:mm");
    let panel = interviewDetails[selectedInterviewId]?.panel || interview.panel;
    if (!panel) {
      const data = await fetchcandidatebyInterviewId(selectedInterviewId);
      if (!data) return;
      panel = data.interview?.panel || [];
    }
    setPanelMemberIds(panel.map((member) => member.user_id).filter(Boolean));

    const storedType = (
      interviewDetails?.interview?.interview_type ||
      interviewDetails?.interview?.interviewType ||
      interview.interview_type ||
      interview.interviewType ||
      ""
    )
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    const meetingLink =
      interviewDetails?.interview?.meeting_link ?? interview.meetingLink ?? "";
    const interviewType =
      storedType === "online"
        ? "online"
        : ["offline", "inperson"].includes(storedType)
          ? "offline"
          : meetingLink
            ? "online"
            : "offline";

    scheduleformik.setValues(
      normalizeInterviewValues({
        candidateName: candidate?.candidate?.name || "",
        appliedJob: interview.position || "",
        email: candidate?.candidate?.email || "",
        contactNo: candidate?.candidate?.phone || "",
        interviewPerson: interview.interviewer_name || "",
        sendEmail: true,
        companyName:
          interviewDetails?.interview?.company_name ??
          interviewDetails?.interview?.companyName ??
          interview.company_name ??
          interview.companyName ??
          "",
        interviewDate: interview.date
          ? (() => {
              const [year, month, day] = interview.date
                .slice(0, 10)
                .split("-")
                .map(Number);

              return new Date(year, month - 1, day);
            })()
          : null,
        startTime: startTimeFormatted,
        endTime: endTimeFormatted,
        address1: interview.address || "",
        address2: interview.address2 || "",
        country: interview.country || "",
        state: interview.state || "",
        city: interview.city || "",
        zipcode: interview.zipCode || "",
        meetingLink,
        timezone: interviewTimezone,
        interviewType,
      }),
    );

    setIsRescheduling(true);
    setShowModal(true);
    setShowCandidateDetails(false);
  };

  const fetchInterviewByCanId = useCallback(async () => {
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${INTERVIEW_BY_CANDIDATE}?candidate_id=${candidateId}&limit=30&offset=0`,
      );
      const groups = response?.groups || [];
      const calendarEvents = groups.flatMap((group) => {
        const { candidate, job, application, interviews = [] } = group;
        return interviews.map((interview) => ({
          ...interview,
          id: interview.id,
          title: `${candidate?.name || interview.candidate_name || "Candidate"} - ${
            job?.title || "Job"
          }`,
          candidateName: candidate?.name || interview.candidate_name || "",
          position: job?.title || "",
          background:
            "linear-gradient(rgb(158 153 235) 0%, rgb(66, 56, 224) 100%)",
          textColor: "#ffffff",
          interviewer_name: interview.interviewer_name || "",
          interviewer_email: interview.interviewer_email || "",
          interviewer_phone: interview.interviewer_phone || "",
          meetingLink: interview.meeting_link || interview.meetingLink || "",
          start: convertUTCTimeToUserTimezone(
            interview.date,
            interview.time_start,
            interview.timezone,
          ),
          end: convertUTCTimeToUserTimezone(
            interview.date,
            interview.time_end,
            interview.timezone,
          ),
          date: interview.date,
          address: interview.address || "",
          address2: interview.address2 || interview.address_2 || "",
          city: interview.city || "",
          country: interview.country || "",
          state: interview.state || "",
          zipCode: interview.zip_code || interview.zipCode || "",
          applicationId: interview.application_id,
          timezone: interview.timezone,
          application,
          candidate,
          job,
        }));
      });
      return {
        groups,
        calendarEvents,
      };
    } catch (error) {
      console.error("Failed to fetch interviews by candidate:", error);
      return null;
    }
  }, [candidateId]);

  const refetchInterviews = useCallback(async () => {
    if (!candidateId) {
      setIsInterviewsLoading(false);
      return;
    }
    const requestId = ++interviewsRequestRef.current;
    setIsInterviewsLoading(true);
    setInterviewsError("");
    try {
      const interviewData = await fetchInterviewByCanId();
      if (requestId !== interviewsRequestRef.current) return;
      if (!interviewData) {
        setInterviewsError("Unable to load interviews. Please try again.");
        return;
      }
      setInterviewDetails({});
      setDetailsError("");
      setInterviewsList(interviewData.calendarEvents);
      setEvents(interviewData.calendarEvents);
    } finally {
      if (requestId === interviewsRequestRef.current) {
        setLoadedCandidateId(candidateId);
        setIsInterviewsLoading(false);
      }
    }
  }, [candidateId, fetchInterviewByCanId]);
  useEffect(() => {
    refetchInterviews();
    return () => {
      interviewsRequestRef.current += 1;
    };
  }, [refetchInterviews]);
  useEffect(() => {
    if (displayView !== "detailed" || isListLoading || interviewsError) return;
    const missingEvents = events.filter(
      (event) =>
        event.id &&
        !Object.prototype.hasOwnProperty.call(interviewDetails, event.id),
    );
    if (!missingEvents.length) return;
    let cancelled = false;
    Promise.all(
      missingEvents.map(async (event) => {
        const response = await get(VYLABS_BASE_URL, GET_INTERVIEWS_BY_ID, {
          params: { id: event.id },
        });
        if (!response?.interview)
          throw new Error("Interview details unavailable");
        return [event.id, response.interview];
      }),
    )
      .then((details) => {
        if (!cancelled) {
          setInterviewDetails((current) => ({
            ...current,
            ...Object.fromEntries(details),
          }));
          setDetailsError("");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to fetch interview details:", error);
          setDetailsError(
            "Unable to load interview details. Please try again.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    events,
    displayView,
    interviewDetails,
    isListLoading,
    interviewsError,
    detailsRetryKey,
  ]);

  const handleJobFilterChange = (value) => {
    const selectedOption =
      jobFilterOptions.find((option) => option.value === value) || null;
    setSelectedFilterJob(selectedOption);
  };

  const fetchCandidateJobs = useCallback(async () => {
    const candidateIdToUse = candidate?.candidate?.id || candidate?.id;

    if (!candidateIdToUse) return [];

    try {
      const fetchResponse = await get(
        VYLABS_BASE_URL,
        `${JOBS_BY_CANDIDATE}?candidate_id=${candidateIdToUse}&limit=30&offset=0`,
      );
      const { jobs = [] } = fetchResponse || {};
      if (Array.isArray(jobs) && jobs.length > 0) {
        const uniqueJobs = [
          ...new Map(
            jobs
              .filter((item) => item?.job?.title && item?.application?.id)
              .map((item) => [
                item.job?.id || item.application?.job_id || item.application.id,
                {
                  label: item.job.title,
                  wrappedLabel: truncateText(item.job.title, 60),
                  value: item.application.id,
                  job_id: item.job?.id || item.application?.job_id,
                },
              ]),
          ).values(),
        ];

        setJobFilterOptions(uniqueJobs);

        if (uniqueJobs.length === 1) {
          setSelectedFilterJob(uniqueJobs[0]);
        } else {
          setSelectedFilterJob(null);
        }

        return uniqueJobs;
      }

      setJobFilterOptions([]);
      setSelectedFilterJob(null);
      return [];
    } catch (error) {
      console.error("Error fetching candidate jobs for scheduler:", error);
      return [];
    }
  }, [candidate?.candidate?.id, candidate?.id]);
  const fetchcandidatebyInterviewId = async (interviewId) => {
    setInterviewID(interviewId);
    try {
      const response = await get(VYLABS_BASE_URL, GET_INTERVIEWS_BY_ID, {
        params: { id: interviewId },
      });
      const {
        interviews = [],
        candidate,
        job,
        application,
        interview,
      } = response;

      const calendarEvents =
        interviews && interviews.length > 0
          ? interviews.map((item) => ({
              id: item.id,
              application_id: item.application_id,
              title: `${item.candidateName} - Interview`,
              position: job?.title || "",
              interviewPerson: item.interviewer_name || "",
              end: convertUTCTimeToUserTimezone(item.date, item.time_end),
              start: convertUTCTimeToUserTimezone(item.date, item.time_start),
            }))
          : [];

      setStorePanelData(interview?.panel || []);
      return {
        application,
        candidate,
        job,
        interviews,
        calendarEvents,
        interview,
      };
    } catch (error) {
      console.error("Failed to fetch interviews by interview ID:", error);
      return null;
    }
  };

  const handlePhoneInputChange = React.useCallback(
    (phone) => {
      scheduleformik.setFieldValue("contactNo", phone);
      scheduleformik.setFieldTouched("contactNo", true, false);
      scheduleformik.validateField("contactNo");
    },
    [scheduleformik],
  );
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };
  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true);
    setShowCandidateDetails(false);
  };

  const EventComponent = ({ event, onClick }) => {
    const eventTimezone = event.timezone;
    const startTime = moment(event.start).tz(eventTimezone).format("h:mm A");
    const endTime = moment(event.end).tz(eventTimezone).format("h:mm A");
    return (
      <div className="interview-event-container" onClick={onClick}>
        <div className="event-header">
          <div className="event-time">
            {startTime} - {endTime} ({eventTimezone})
          </div>
        </div>
        <div className="event-candidate">
          <User size="1em" className="event-icon" />
          <span>
            {event.candidateName || event.candidate_name || "Candidate"}
          </span>
        </div>
        <div
          className="event-indicator"
          style={{
            width: "40%",
            height: "3px",
            background: "rgba(255,255,255,0.3)",
            borderRadius: "2px",
            marginTop: "6px",
          }}
        ></div>
      </div>
    );
  };

  const hasAssignedJob = (latestJobs) =>
    Array.isArray(latestJobs) && latestJobs.length > 0;

  const handleAddEventClick = async () => {
    if (isMonthlyEmailLimitReached) {
      Toast("error", "Monthly email limit reached. Please upgrade your plan.");
      return;
    }
    if (scheduleformik.values.email === "") {
      Toast("error", "Candidate email is required");
      return;
    }
    const latestJobs = await fetchCandidateJobs();

    if (!hasAssignedJob(latestJobs)) {
      Toast(
        "error",
        "No job assigned to this candidate. Please assign a job before creating an interview schedule.",
      );
      return;
    }

    const now = new Date();
    const roundedStart = new Date(now);
    if (now.getMinutes() < 30) {
      roundedStart.setMinutes(30);
    } else {
      roundedStart.setHours(now.getHours() + 1);
      roundedStart.setMinutes(0);
    }
    roundedStart.setSeconds(0);
    roundedStart.setMilliseconds(0);

    const thirtyMinutesLater = new Date(
      roundedStart.getTime() + 30 * 60 * 1000,
    );

    setPanelMemberIds([]);
    scheduleformik.setFieldValue("contactNo", "");
    scheduleformik.resetForm();
    scheduleformik.setValues({
      ...scheduleformik.initialValues,
      interviewDate: now,
      startTime: moment(roundedStart).format("HH:mm"),
      endTime: moment(thirtyMinutesLater).format("HH:mm"),
      timezone: currentTimezone,
      interviewType: "online",
      meetingLink: "",
      address1: "",
      address2: "",
      country: "",
      state: "",
      city: "",
      zipcode: "",
    });
    setIsRescheduling(false);
    setShowModal(true);
  };

  const fetchEmailPreview = async () => {
    const values = normalizeInterviewValues(scheduleformik.values);
    const applicationID =
      selectedFilterJob?.value ||
      applicationId ||
      candidate?.application_ids?.[0];

    if (!applicationID) {
      Toast("error", "No valid application ID found for this candidate");
      return;
    }
    setPreviewData((prev) => ({ ...prev, isLoading: true }));
    try {
      const rawPayload = {
        address: values.address1,
        applicationID: applicationID,
        city: values.city,
        companyName: values.companyName,
        country: values.country,
        date: values.interviewDate
          ? `${values.interviewDate.getFullYear()}-${String(
              values.interviewDate.getMonth() + 1,
            ).padStart(2, "0")}-${String(
              values.interviewDate.getDate(),
            ).padStart(2, "0")}`
          : null,
        interviewerName: values.interviewPerson,
        meetingLink: values.meetingLink,
        state: values.state,
        timeEnd: values.endTime,
        timeStart: values.startTime,
        timezone: values.timezone,
        zipCode: values.zipcode?.toString() || "",
      };
      const payload = filterPayload(rawPayload);
      const response = await post(
        VYLABS_BASE_URL,
        INTERVIEW_EMAIL_PREVIEW,
        payload,
      );

      if (response) {
        setPreviewData({
          body: response.body || "",
          subject: response.subject || "Interview Confirmation",
          isLoading: false,
        });
        setIsEmailPreviewOpen(true);
      }
    } catch (error) {
      console.error("Error fetching email preview:", error);
      Toast("error", error.message || "Failed to load email preview");
      setPreviewData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const canAccessPreview = () => {
    const values = scheduleformik.values;
    const hasRequiredBase =
      values.interviewPerson?.trim() &&
      values.interviewDate &&
      values.startTime &&
      values.endTime;

    const hasMeetingInfo =
      values.interviewType === "online"
        ? values.meetingLink?.trim()
        : values.address1?.trim();

    return hasRequiredBase && hasMeetingInfo;
  };

  const handleSelectEvent = async (interview) => {
    const interviewId = interview?.id;
    if (!interviewId) {
      console.error("Missing interview ID in interview:", interview);
      return;
    }

    const data = await fetchcandidatebyInterviewId(interviewId);

    if (data && data.candidate) {
      const selectedCandidateData = {
        name: data.candidate.name || "Unknown Candidate",
        email: data.candidate.email || "N/A",
        contactNo: data.candidate.phone || "N/A",
        position: data.job?.title || "N/A",
        interviewPerson: interview.interviewer_name || "N/A",
        startTime: moment(interview.start).format("h:mm A"),
        endTime: moment(interview.end).format("h:mm A"),
        date: moment(interview.start).format("MMMM D, YYYY"),
        meetingLink: interview.meetingLink || interview.meeting_link || "",
        interviewType: interview.meetingLink ? "online" : "in person",
        timezone: interview.timezone || "N/A",
      };

      setSelectedCandidate(selectedCandidateData);
      setStorePanelData(data?.interview?.panel || []);
      setSelectedIid(interviewId);
      setShowCandidateDetails(true);
    }
  };

  const handleCloseCandidateDetails = () => {
    setShowCandidateDetails(false);
    setSelectedCandidate(null);
    setIsFeedbackPopupOpen(false);
    setStorePanelData([]);
  };

  const createInterviewPanelFeedback = async () => {
    try {
      await put(VYLABS_BASE_URL, INTERVIEW_PANEL_FEEDBACK, {
        interview_id: interviewID,
        notes: feedbackFormik.values.feedbackNotes,
        rating: feedbackFormik.values.score,
        hiring_recommendation: feedbackFormik.values.hiringRecommendation,
      });
      await fetchcandidatebyInterviewId(interviewID);
      await refetchInterviews();
      Toast("success", "Feedback submitted successfully!");
      setIsFeedbackPopupOpen(false);
      feedbackFormik.resetForm();
    } catch (error) {
      console.error("error in createInterviewPanelFeedback", error);
      Toast("error", error.response?.data || "Failed to submit feedback");
    }
  };

  const CustomToolbar = ({ label }) => {
    return (
      <div className="toolbar">
        <div
          className="custom-toolbar"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <img
            alt=""
            src={isDarkMode ? darkCalendarIcon : calendarIcon}
            style={{ width: "25px", marginRight: "10px", cursor: "pointer" }}
            ref={calendarIconRef}
            onClick={openDatePicker}
          ></img>
          <div className="calender-toolbar-label">{label}</div>
          <div
            className="calendar-navigation"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2vw",
              position: "absolute",
              right: 0,
            }}
          >
            <StyledButton
              className="navigate-btn previous-btn"
              onClick={() => navigateByView("prev")}
              style={{
                border: "1px solid var(--border__color)",
                borderRadius: "5px",
                background: "var(--navigation__bg)",
                color: "var(--primary__color)",
                cursor: "pointer",
                padding: "0px",
                height: "30px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ChevronLeft size={20} strokeWidth={3} />
            </StyledButton>
            <StyledButton
              onClick={() => navigateByView("next")}
              style={{
                border: "1px solid var(--border__color)",
                borderRadius: "5px",
                background: "var(--navigation__bg)",
                color: "var(--primary__color)",
                cursor: "pointer",
                padding: "0px",
                height: "30px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ChevronRight size={20} strokeWidth={3} />
            </StyledButton>
          </div>
        </div>
      </div>
    );
  };
  const handleTimezoneChange = (newTimezone) => {
    scheduleformik.setFieldValue("timezone", newTimezone);
  };

  const handleDeleteEvent = async () => {
    try {
      await deleteRequest(VYLABS_BASE_URL, DELETE_INTERVIEW, {
        id: interviewID,
      });
      await refetchInterviews();

      Toast("success", "Event deleted successfully");
    } catch (error) {
      console.error("Error deleting event:", error);
    } finally {
      setShowDeleteModal(false);
      setShowCandidateDetails(false);
    }
  };

  const handleSelectSlot = async ({ start, end }) => {
    const latestJobs = await fetchCandidateJobs();

    if (!hasAssignedJob(latestJobs)) {
      Toast(
        "error",
        "No job assigned to this candidate. Please assign a job before creating an interview schedule.",
      );
      return;
    }
    if (!isDragging) {
      scheduleformik.setFieldValue("sendEmail", true);
      setNewEvent({
        ...newEvent,
        start: new Date(start),
        end: new Date(end),
      });
      setShowModal(true);
    }
  };

  const handleSaveEvent = () => {
    const values = normalizeInterviewValues(scheduleformik.values);
    const hasErrors = Object.keys(scheduleformik.errors).length > 0;
    const isTouched = Object.keys(scheduleformik.touched).length > 0;

    if (hasErrors && isTouched) {
      Toast("error", "Please fill all the required fields correctly");
      return;
    }
    if (values.interviewType === "online" && !values.meetingLink) {
      Toast("error", "Meeting link is required for online interviews");
      return;
    }

    if (values.interviewType === "offline" && !values.address1) {
      Toast("error", "Address is required for in-person interviews");
      return;
    }

    if (values.interviewType === "offline" && !values.country) {
      Toast("error", "Country is required for in-person interviews");
      return;
    }

    if (values.interviewType === "offline" && !values.state) {
      Toast("error", "State is required for in-person interviews");
      return;
    }

    if (values.interviewType === "offline" && !values.city) {
      Toast("error", "City is required for in-person interviews");
      return;
    }

    if (values.interviewType === "offline" && !values.zipcode) {
      Toast("error", "Zipcode is required for in-person interviews");
      return;
    }

    if (isRescheduling) {
      updatingInterview();
    } else {
      scheduleformik.handleSubmit();
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewEvent({ title: "", start: null, end: null });
    setPreviewData({ body: "", subject: "", isLoading: false });
    setSelectedFilterJob(null);
    setPanelMemberIds([]);
  };
  const navigateByView = (direction) => {
    const newDate = new Date(selectedDate);

    switch (currentView) {
      case Views.DAY:
        const dayOffset = direction === "prev" ? -1 : 1;
        newDate.setDate(newDate.getDate() + dayOffset);
        break;

      case Views.WEEK:
        const weekOffset = direction === "prev" ? -7 : 7;
        newDate.setDate(newDate.getDate() + weekOffset);
        break;

      case Views.MONTH:
        if (direction === "prev") {
          newDate.setMonth(newDate.getMonth() - 1);
        } else {
          newDate.setMonth(newDate.getMonth() + 1);
        }
        break;

      default:
        break;
    }

    setSelectedDate(newDate);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const moveEvent = useCallback(({ event, start, end }) => {
    setEvents((prevEvents) => {
      const idx = prevEvents.indexOf(event);
      const updatedEvent = { ...event, start, end };
      const nextEvents = [...prevEvents];
      nextEvents.splice(idx, 1, updatedEvent);
      return nextEvents;
    });
  }, []);

  const resizeEvent = useCallback(({ event, start, end }) => {
    setEvents((prevEvents) => {
      const idx = prevEvents.indexOf(event);
      const updatedEvent = { ...event, start, end };
      const nextEvents = [...prevEvents];
      nextEvents.splice(idx, 1, updatedEvent);
      return nextEvents;
    });
  }, []);

  const onDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onDragEnd = useCallback(() => {
    setTimeout(() => setIsDragging(false), 100);
  }, []);
  const getViewName = (view) => {
    switch (view) {
      case Views.DAY:
        return "Day";
      case Views.WEEK:
        return "Week";
      case Views.MONTH:
        return "Month";
      default:
        return view;
    }
  };
  const ViewSelector = () => (
    <div className="view-selector" style={{ display: "flex", gap: "20px" }}>
      {[Views.DAY, Views.WEEK, Views.MONTH].map((view) => (
        <StyledButton
          key={view}
          type="button"
          aria-pressed={currentView === view}
          onClick={() => handleViewChange(view)}
          style={{
            padding: "12px 20px",
            borderRadius: "8px",
            background:
              currentView === view
                ? "var(--primary__color)"
                : "var(--body__background)",
            color: currentView === view ? "white" : "var(--header__color)",
            cursor: "pointer",
            border: "1px solid var(--border__color)",
            fontWeight: "500",
          }}
        >
          {getViewName(view)}
        </StyledButton>
      ))}
    </div>
  );
  const TimeGutterHeader = () => (
    <div style={{ height: "100%", padding: "8px" }} />
  );
  const CustomEventWrapper = ({ children, event }) => {
    const overlappingEvents = events.filter(
      (ev) =>
        ev.id !== event.id &&
        ((ev.start < event.end && ev.end > event.start) ||
          (event.start < ev.end && event.end > ev.start)),
    ).length;

    return React.cloneElement(React.Children.only(children), {
      style: {
        ...children.props.style,
        zIndex: 100 + overlappingEvents,
        width:
          overlappingEvents > 0
            ? `calc(100% - ${overlappingEvents * 5}px)`
            : "100%",
        marginLeft: overlappingEvents * 5,
      },
    });
  };

  const MonthEventComponent = ({ event }) => (
    <div className="month-interview-event">
      <div className="month-event-time">
        {moment(event.start).format("h:mm A")}
      </div>
      <div className="month-event-name">{event.candidateName || ""}</div>
    </div>
  );

  const openDatePicker = () => {
    if (calendarIconRef.current) {
      const iconRect = calendarIconRef.current.getBoundingClientRect();
      const adjustedLeft = Math.min(iconRect.left, window.innerWidth - 350);
      setPickerPosition({
        top: iconRect.bottom + 6,
        left: adjustedLeft,
      });
      requestAnimationFrame(() => {
        setActiveDatePicker("toolbar");
        setIsDatePickerOpen(true);
      });
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) {
      return "";
    }
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };
  useEffect(() => {
    if (!isDatePickerOpen) return;
    const handleResize = () => openDatePicker();
    window.addEventListener("resize", handleResize);

    const handleClickOutside = (event) => {
      if (pickerRef.current && pickerRef.current.contains(event.target)) {
        return;
      }
      if (
        calendarIconRef.current &&
        calendarIconRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        interviewDateRef.current &&
        interviewDateRef.current.contains(event.target)
      ) {
        return;
      }

      if (!event.target.closest(".date-picker-modal")) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);

  const showInterviewFeedbackButton = storePanelData.find(
    (data) => userDetails.id === data.user_id,
  );
  useEffect(() => {
    if (!isDatePickerOpen || !calendarContainerRef.current) return;
    const container = calendarContainerRef.current;
    const handleScrollClose = () => setIsDatePickerOpen(false);
    container.addEventListener("scroll", handleScrollClose, true);
    return () =>
      container.removeEventListener("scroll", handleScrollClose, true);
  }, [isDatePickerOpen]);

  return (
    <div
      className="calendar-container candidate-interview-scheduler"
      ref={calendarContainerRef}
      style={{
        height: "80vh",
        boxShadow: "0px 4px 10px 0px #0000002A",
        borderRadius: "10px",
        overflow: "auto",
      }}
    >
      <div className="candidate-scheduler-header">
        <div className="candidate-scheduler-heading">
          <h3>Scheduled Interviews</h3>
          <p>
            Manage interview rounds, internal panel assignments, feedback, and
            calendar links.
          </p>
        </div>
        <div className="candidate-scheduler-actions">
          <div
            className="filter-stages candidate-scheduler-toggle"
            role="group"
            aria-label="Interview display view"
          >
            <StyledButton
              type="button"
              className={`grid-btn left ${displayView === "detailed" ? "active" : ""}`}
              aria-pressed={displayView === "detailed"}
              aria-controls={schedulerContentId}
              onClick={() => {
                setDisplayView("detailed");
                setIsDatePickerOpen(false);
              }}
            >
              <DetailedViewIcon
                className="candidate-interview-asset-icon"
                aria-hidden="true"
                focusable="false"
              />
              Detailed View
            </StyledButton>
            <StyledButton
              type="button"
              className={`grid-btn right ${displayView === "calendar" ? "active" : ""}`}
              aria-pressed={displayView === "calendar"}
              aria-controls={schedulerContentId}
              onClick={() => {
                setDisplayView("calendar");
                setIsDatePickerOpen(false);
              }}
            >
              <CalendarViewIcon
                className="candidate-interview-asset-icon"
                aria-hidden="true"
                focusable="false"
              />
              Calendar View
            </StyledButton>
          </div>
          <div className="schedule-interview-container">
            <CustomTooltip
              title={getMonthlyEmailTooltip(
                "Schedule an interview with this candidate.",
              )}
              arrow
            >
              <span>
                <Button
                  event={handleAddEventClick}
                  children={<CalendarDays size="1em" aria-hidden="true" />}
                  label="Schedule Interview"
                  style={{
                    opacity: isMonthlyEmailLimitReached ? 0.6 : 1,
                    fontFamily: "inherit",
                  }}
                  disabled={isMonthlyEmailLimitReached}
                />
              </span>
            </CustomTooltip>
          </div>
        </div>
      </div>
      <section
        id={schedulerContentId}
        className="candidate-scheduler-content"
        aria-busy={isSchedulerLoading}
      >
        {isListLoading || (!schedulerError && isDetailsLoading) ? (
          <div
            className="candidate-scheduler-loading"
            role="status"
            aria-live="polite"
          >
            <VybogTALLoaderAnimation
              animationHeight="9.375rem"
              animationWidth="9.375rem"
            />
          </div>
        ) : schedulerError ? (
          <div className="candidate-scheduler-error" role="alert">
            <p>{schedulerError}</p>
            <Button
              label="Try again"
              event={() => {
                if (interviewsError) refetchInterviews();
                else {
                  setDetailsError("");
                  setDetailsRetryKey((key) => key + 1);
                }
              }}
            />
          </div>
        ) : displayView === "detailed" ? (
          <CandidateInterviewDetails
            events={events}
            details={interviewDetails}
            candidateName={candidate?.candidate?.name || candidate?.name}
            onViewInterview={handleSelectEvent}
            canGiveFeedback={isHiringTeamTenant}
            userId={userDetails?.id}
            onFeedback={(interview, member) => {
              setInterviewID(interview.id);
              setFeedbackMember(member);
              setIsFeedbackPopupOpen(true);
            }}
          />
        ) : (
          <>
            <div
              className={`${headerClassName} candidate-scheduler-calendar-header`}
            >
              <div className="view-and-button">
                <ViewSelector />
              </div>
            </div>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable
              views={[Views.MONTH, Views.WEEK, Views.DAY]}
              date={selectedDate}
              onNavigate={setSelectedDate}
              onView={handleViewChange}
              view={currentView}
              className="bg-white rounded-lg"
              defaultView={Views.DAY}
              draggableAccessor={() => true}
              resizable={false}
              onEventDrop={moveEvent}
              onEventResize={resizeEvent}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              timeslots={4}
              step={15}
              components={{
                event: EventComponent,
                timeGutterHeader: TimeGutterHeader,
                toolbar: CustomToolbar,
                eventWrapper: CustomEventWrapper,
                month: {
                  event: MonthEventComponent,
                },
              }}
              eventPropGetter={(event) => {
                const overlappingCount = events.filter(
                  (ev) =>
                    ev.id !== event.id &&
                    ((ev.start < event.end && ev.end > event.start) ||
                      (event.start < ev.end && event.end > ev.start)),
                ).length;

                return {
                  style: {
                    background:
                      overlappingCount > 0
                        ? `linear-gradient(135deg, #5568ee - (overlappingCount * 111111)}, #${4238 - overlappingCount * 111111})`
                        : "linear-gradient(135deg, #6860ee 0%, #4238e0 100%)",
                    color: "white",
                    borderRadius: "5px",
                    boxShadow:
                      overlappingCount > 0
                        ? "0 0 0 1px rgba(0,0,0,0.1)"
                        : "none",
                  },
                };
              }}
            />
          </>
        )}
      </section>
      <SidePopup
        isOpen={showModal}
        onClose={handleCloseModal}
        title={isRescheduling ? "Reschedule Interview" : "Schedule Interview"}
        sidePopupWidth={"50%"}
      >
        <div className="candidate-details-outer">
          <div className="candidate-details-container">
            <h3 className="schedule-header">Candidate Details</h3>
            <div className="candidate-fields-container">
              {/* <div className="candidate-name-container">
                <span className="select-candidate">Select Candidate</span>
                <Textfield
                  label={"Candidate Name"}
                  placeholder={"Candidate Name"}
                  name={"candidateName"}
                  value={scheduleformik.values.candidateName}
                  error={
                    scheduleformik.errors.candidateName &&
                    scheduleformik.touched.candidateName
                  }
                  errorMessage={scheduleformik.errors.candidateName}
                  required={true}
                  style={{ width: "100%" }}
                  onChange={scheduleformik.handleChange}
                  onBlur={scheduleformik.handleBlur}
                />
              </div>

              <div className="candidate-email-container">
                <Textfield
                  type="email"
                  label={"Email"}
                  placeholder={"Email"}
                  required={true}
                  name={"email"}
                  value={scheduleformik.values.email}
                  error={
                    scheduleformik.errors.email && scheduleformik.touched.email
                  }
                  errorMessage={scheduleformik.errors.email}
                  onChange={scheduleformik.handleChange}
                  onBlur={scheduleformik.handleBlur}
                />
                <PhoneNumberInput
                  value={scheduleformik.values.contactNo}
                  onChange={handlePhoneInputChange}
                  onBlur={scheduleformik.handleBlur}
                  error={scheduleformik.errors.contactNo}
                  touched={scheduleformik.touched.contactNo}
                  placeholder="Contact No"
                  label="Contact No"
                  height="41px"
                  countryListWidth="200px"
                  phoneContainerStyle={{ marginBottom: "14px" }}
                  required={true}
                />
              </div> */}    
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <b style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <img src={candidateIcon} alt="Candidate" style={{ width: "18px", height: "18px" }} />
                  Name
                </b>
                <span>{scheduleformik.values.candidateName || "N/A"}</span>

                <b style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <img src={emailIcon} alt="Email" style={{ width: "18px", height: "18px" }} />
                  Email
                </b>
                <span>{scheduleformik.values.email || "N/A"}</span>

                <b style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <img src={contactNoIcon} alt="Phone" style={{ width: "18px", height: "18px" }} />
                  Phone
                </b>
                <span>{scheduleformik.values.contactNo || "N/A"}</span>
              </div>
              {jobFilterOptions.length > 1 && !isRescheduling && (
                <div>
                  <StyleDOM.TrackingUpperOuterContainer>
                    <div className="history-info-container">
                      Job Filter
                      <CustomTooltip
                        title={
                          "Interview will be scheduled to the job you select."
                        }
                        arrow
                      >
                        {" "}
                        <img
                          src={filterInfoIcon}
                          alt="Action 2"
                          style={{
                            width: "20px",
                            height: "20px",
                            margin: "4px 5px -4px",
                          }}
                        />
                      </CustomTooltip>
                    </div>
                    <SelectInput
                      placeholder="Interview will be scheduled to the job you select."
                      onChange={(e) => handleJobFilterChange(e.target.value)}
                      value={selectedFilterJob?.value || ""}
                      options={jobFilterOptions}
                      labelKey="wrappedLabel"
                      labelValue="value"
                      enableTooltip={true}
                      wrapperStyle={{ marginTop: "10px" }}
                    />
                  </StyleDOM.TrackingUpperOuterContainer>
                </div>
              )}
            </div>
          </div>
          <div className="candidate-details-container">
            <div className="interview-toogle-container">
              <h3 className="schedule-header">Interview Details</h3>
              <div className="interview-type-container">
                <div
                  style={{
                    display: "inline-flex",
                    backgroundColor: "var(--tab__background)",
                    borderRadius: "8px",
                    padding: "4px",
                    marginTop: "8px",
                  }}
                >
                  <Button
                    event={() => handleInterviewTypeChange("online")}
                    style={{
                      padding: "8px 20px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor:
                        scheduleformik.values.interviewType === "online"
                          ? "var(--primary__color)"
                          : "transparent",
                      color:
                        scheduleformik.values.interviewType === "online"
                          ? "white"
                          : "var(--header__color)",
                      fontWeight: "500",
                      fontSize: "var(--candidate-interview-font-14, .875rem)",
                      cursor: "pointer",
                      boxShadow:
                        scheduleformik.values.interviewType === "online"
                          ? "0 2px 8px rgba(104, 96, 238, 0.3)"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <VideoCallIcon fontSize="small" />
                    Online
                  </Button>
                  <Button
                    event={() => handleInterviewTypeChange("offline")}
                    style={{
                      padding: "8px 20px",
                      border: "none",
                      borderRadius: "6px",
                      backgroundColor:
                        scheduleformik.values.interviewType === "offline"
                          ? "var(--primary__color)"
                          : "transparent",
                      color:
                        scheduleformik.values.interviewType === "offline"
                          ? "white"
                          : "var(--header__color)",
                      fontWeight: "500",
                      fontSize: "var(--candidate-interview-font-14, .875rem)",
                      cursor: "pointer",
                      boxShadow:
                        scheduleformik.values.interviewType === "offline"
                          ? "0 2px 8px rgba(104, 96, 238, 0.3)"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <LocationOnIcon fontSize="small" />
                    In-Person
                  </Button>
                </div>
                {scheduleformik.errors.interviewType &&
                  scheduleformik.touched.interviewType && (
                    <div
                      className="time-error-message"
                      style={{
                        marginTop: "8px",
                        fontSize: "var(--candidate-interview-font-12, .75rem)",
                        color: "var(--chip-text-not-suitable)",
                      }}
                    >
                      {scheduleformik.errors.interviewType}
                    </div>
                  )}
              </div>
            </div>
            <div className="candidate-fields-container">
              <div className="candidate-name-container">
                <Textfield
                  label={
                    isHiringTeamTenant ? "Coordinator" : "Interviewer"
                  }
                  placeholder={
                    isHiringTeamTenant ? "Coordinator" : "Interviewer"
                  }
                  style={{ width: "100%" }}
                  name={"interviewPerson"}
                  value={scheduleformik.values.interviewPerson}
                  error={
                    scheduleformik.errors.interviewPerson &&
                    scheduleformik.touched.interviewPerson
                  }
                  errorMessage={scheduleformik.errors.interviewPerson}
                  onChange={scheduleformik.handleChange}
                  onBlur={scheduleformik.handleBlur}
                />
                {isHiringTeamTenant ? null : (
                  <Textfield
                    label={"Company Name"}
                    placeholder={"company name"}
                    style={{ width: "100%" }}
                    name={"companyName"}
                    value={scheduleformik.values.companyName}
                    error={
                      scheduleformik.errors.companyName &&
                      scheduleformik.touched.companyName
                    }
                    errorMessage={scheduleformik.errors.companyName}
                    onChange={scheduleformik.handleChange}
                    onBlur={scheduleformik.handleBlur}
                  />
                )}
              </div>
              <div className="candidate-email-container">
                <div className="date-time-container">
                  <label className="interview-labels">
                    Interview{" "}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {" "}
                      Date <span style={{ color: "red" }}>*</span>
                    </span>
                  </label>
                  <span>:</span>
                </div>
                <div className="time-and-error-container">
                  <div className="time-container">
                    <img
                      alt=""
                      src={isDarkMode ? darkCalendarIcon : calendarIcon}
                      className="calendarschedulerIcons"
                    />
                    <div
                      className="calendar-date-picker"
                      ref={interviewDateRef}
                      onClick={() => {
                        const rect =
                          interviewDateRef.current.getBoundingClientRect();
                        setPickerPosition({
                          top: rect.bottom + 6,
                          left: rect.left,
                        });
                        requestAnimationFrame(() => {
                          setActiveDatePicker("interview");
                          setIsDatePickerOpen(true);
                        });
                      }}
                    >
                      {scheduleformik.values.interviewDate
                        ? new Date(
                            scheduleformik.values.interviewDate.getFullYear(),
                            scheduleformik.values.interviewDate.getMonth(),
                            scheduleformik.values.interviewDate.getDate(),
                          ).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "Select date"}
                    </div>
                  </div>
                  {scheduleformik.errors.interviewDate &&
                    scheduleformik.touched.interviewDate && (
                      <div className="time-error-message">
                        {scheduleformik.errors.interviewDate}
                      </div>
                    )}
                </div>

                <div className="date-time-container">
                  <label className="interview-labels">
                    Start{" "}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {" "}
                      Time <span style={{ color: "red" }}>*</span>
                    </span>
                  </label>
                  <span>:</span>
                </div>
                <div className="time-and-error-container">
                  <CustomTimePicker
                    selectedTime={scheduleformik.values.startTime}
                    onChange={(time) => {
                      scheduleformik.setFieldValue("startTime", time);
                      scheduleformik.setFieldTouched("startTime", true, false);
                      scheduleformik.validateField("startTime");
                    }}
                    placeholderText="Select start time"
                    style={{ marginBottom: "10px" }}
                  />
                  {scheduleformik.errors.startTime &&
                    scheduleformik.touched.startTime && (
                      <div className="time-error-message">
                        {scheduleformik.errors.startTime}
                      </div>
                    )}
                </div>
                <div className="date-time-container">
                  <label className="interview-labels">
                    End{" "}
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      Time <span style={{ color: "red" }}>*</span>
                    </span>
                  </label>
                  <span>:</span>
                </div>
                <div className="time-and-error-container">
                  <CustomTimePicker
                    selectedTime={scheduleformik.values.endTime}
                    onChange={(time) => {
                      scheduleformik.setFieldValue("endTime", time);
                      scheduleformik.setFieldTouched("endTime", true, false);
                      scheduleformik.validateField("endTime");
                    }}
                    placeholderText="Select end time"
                    style={{ marginBottom: "10px" }}
                  />
                  {scheduleformik.errors.endTime &&
                    scheduleformik.touched.endTime && (
                      <div className="time-error-message">
                        {scheduleformik.errors.endTime}
                      </div>
                    )}
                </div>
                <div className="date-time-container">
                  <label className="interview-labels">
                    Timezone<span style={{ color: "red" }}>*</span>
                    <span
                      style={{
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    ></span>
                  </label>
                  <span>:</span>
                </div>
                <TimezoneSelector
                  selectedTimezone={scheduleformik.values.timezone}
                  onTimezoneChange={handleTimezoneChange}
                />
              </div>
            </div>
          </div>
          {isHiringTeamTenant ? (
            <CandidateInterviewPanel
              selectedIds={panelMemberIds}
              onSelectionChange={setPanelMemberIds}
            />
          ) : null}
          <Accordion
            title={`Interview Meeting Details ${scheduleformik.values.interviewType === "online" ? "(Required)" : "(Not required for in-person interview)"}`}
            titleColor="var(--primary__color)"
            accordionHeaderStyle={{
              background: "var(--accordion-header-bg)",
              color: "var(--header__color)",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "10px",
              opacity:
                scheduleformik.values.interviewType === "offline" ? 0.6 : 1,
            }}
            defaultOpen={scheduleformik.values.interviewType === "online"}
            isDisabled={scheduleformik.values.interviewType === "offline"}
          >
            <div className="candidate-details-container">
              <Textfield
                label="Meeting Link"
                name="meetingLink"
                placeholder="Enter Your Meeting Link (e.g., https://meet.google.com/xxx-yyyy-zzz)"
                value={scheduleformik.values.meetingLink}
                onChange={scheduleformik.handleChange}
                onBlur={scheduleformik.handleBlur}
                disabled={scheduleformik.values.interviewType === "offline"}
                required={scheduleformik.values.interviewType === "online"}
                error={
                  scheduleformik.errors.meetingLink &&
                  scheduleformik.touched.meetingLink
                }
                errorMessage={scheduleformik.errors.meetingLink}
              />
            </div>
          </Accordion>

          <Accordion
            title={`Interview Location Details ${scheduleformik.values.interviewType === "offline" ? "(Required)" : "(Not required for online interview)"}`}
            titleColor="var(--primary__color)"
            accordionHeaderStyle={{
              background: "var(--accordion-header-bg)",
              color: "var(--header__color)",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "10px",
              opacity:
                scheduleformik.values.interviewType === "online" ? 0.6 : 1,
            }}
            defaultOpen={scheduleformik.values.interviewType === "offline"}
            isDisabled={scheduleformik.values.interviewType === "online"}
          >
            <div className="candidate-details-container">
              <div className="schedule-address-container">
                <Textfield
                  label="Address Line 1"
                  name="address1"
                  placeholder="Address Line 1"
                  value={scheduleformik.values.address1}
                  onChange={scheduleformik.handleChange}
                  onBlur={scheduleformik.handleBlur}
                  disabled={scheduleformik.values.interviewType === "online"}
                  required={scheduleformik.values.interviewType === "offline"}
                  error={
                    scheduleformik.errors.address1 &&
                    scheduleformik.touched.address1
                  }
                  errorMessage={scheduleformik.errors.address1}
                />
                <Textfield
                  label="Address Line 2 (Optional)"
                  name="address2"
                  placeholder="Address Line 2"
                  value={scheduleformik.values.address2}
                  onChange={scheduleformik.handleChange}
                  disabled={scheduleformik.values.interviewType === "online"}
                />
                <div className="schedule-location-container">
                  <Textfield
                    label="Country"
                    name="country"
                    placeholder="Country"
                    value={scheduleformik.values.country}
                    onChange={scheduleformik.handleChange}
                    onBlur={scheduleformik.handleBlur}
                    disabled={scheduleformik.values.interviewType === "online"}
                    required={scheduleformik.values.interviewType === "offline"}
                    error={
                      scheduleformik.errors.country &&
                      scheduleformik.touched.country
                    }
                    errorMessage={scheduleformik.errors.country}
                  />
                  <Textfield
                    label="State"
                    name="state"
                    placeholder="State"
                    value={scheduleformik.values.state}
                    onChange={scheduleformik.handleChange}
                    onBlur={scheduleformik.handleBlur}
                    disabled={scheduleformik.values.interviewType === "online"}
                    required={scheduleformik.values.interviewType === "offline"}
                    error={
                      scheduleformik.errors.state &&
                      scheduleformik.touched.state
                    }
                    errorMessage={scheduleformik.errors.state}
                  />

                  <Textfield
                    label="City"
                    name="city"
                    placeholder="City"
                    value={scheduleformik.values.city}
                    onChange={scheduleformik.handleChange}
                    onBlur={scheduleformik.handleBlur}
                    disabled={scheduleformik.values.interviewType === "online"}
                    required={scheduleformik.values.interviewType === "offline"}
                    error={
                      scheduleformik.errors.city && scheduleformik.touched.city
                    }
                    errorMessage={scheduleformik.errors.city}
                  />
                  <Textfield
                    label="Zipcode"
                    type="number"
                    name="zipcode"
                    placeholder="Zipcode"
                    value={scheduleformik.values.zipcode}
                    onChange={scheduleformik.handleChange}
                    onBlur={scheduleformik.handleBlur}
                    disabled={scheduleformik.values.interviewType === "online"}
                    required={scheduleformik.values.interviewType === "offline"}
                    error={
                      scheduleformik.errors.zipcode &&
                      scheduleformik.touched.zipcode
                    }
                    errorMessage={scheduleformik.errors.zipcode}
                  />
                </div>
              </div>
            </div>
          </Accordion>
          <FormControlLabel
            label="Send email to candidate and interviewer"
            control={
              <Switch
                name="sendEmail"
                checked={scheduleformik.values.sendEmail}
                onChange={(_, checked) =>
                  scheduleformik.setFieldValue("sendEmail", checked)
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "var(--primary__color)",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "var(--primary__color)",
                  },
                }}
              />
            }
            sx={{
              margin: "12px 0",
              color: "var(--header__color)",
              "& .MuiFormControlLabel-label": { fontFamily: "inherit" },
            }}
          />
          <div className="schedule-button-container">
            <Button
              label="Cancel"
              style={{ marginRight: "0" }}
              isCancel={true}
              event={handleCloseModal}
            />
            <CustomTooltip
              title={
                canAccessPreview()
                  ? "Click to view preview"
                  : "Fill all required fields to view preview"
              }
              arrow
            >
              <span>
                <Button
                  label={previewData.isLoading ? "Loading..." : "Email Preview"}
                  event={fetchEmailPreview}
                  backgroundColor="var(--primary__color)"
                  color="#FFFFFF"
                  disabled={!canAccessPreview() || previewData.isLoading}
                  {...((!canAccessPreview() || previewData.isLoading) && {
                    hoverd: false,
                    hoverStyles: {},
                    hoverIconSrc: null,
                    style: {
                      backgroundColor: "var(--disabled-button)",
                      color: "white",
                      cursor: "not-allowed",
                      opacity: 0.7,
                    },
                  })}
                />
              </span>
            </CustomTooltip>
            <Button
              event={handleSaveEvent}
              label={isRescheduling ? "Update Interview" : "Schedule Interview"}
            />
          </div>
        </div>
      </SidePopup>

      <SidePopup
        isOpen={showCandidateDetails}
        onClose={handleCloseCandidateDetails}
        title={selectedCandidate?.position}
        titleColor="var(--sidepopup-header-color)"
        headerBackgroundColor="var(--sidepopup-header-background)"
        sidePopupWidth={"53%"}
        headerRightContent={
          isHiringTeamTenant &&
          storePanelData.length > 0 &&
          showInterviewFeedbackButton !== undefined && (
            <Button
              label="Interview Feedback"
              event={() => {
                setFeedbackMember(
                  storePanelData.find(
                    (member) =>
                      String(member.user_id) === String(userDetails?.id),
                  ) || null,
                );
                setIsFeedbackPopupOpen(true);
              }}
            />
          )
        }
      >
        {selectedCandidate && (
          <div className="candidate-details-popup">
            <div className="candidate-details-outer-container">
              <div className="candidate-details-inner-container">
                <h3 className="schedule-header">Candidate Details</h3>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={candidateIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>Candidate</h4>
                  </div>
                  <p>{applyEllipsis(selectedCandidate.name, 30) || "N/A"}</p>
                </div>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={emailIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>Email</h4>
                  </div>
                  <p>{applyEllipsis(selectedCandidate.email, 30) || "N/A"}</p>
                </div>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={contactNoIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>Contact No.</h4>
                  </div>
                  <p>{selectedCandidate.contactNo || "N/A"}</p>
                </div>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={jobIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>Applied for</h4>
                  </div>
                  <p>
                    {applyEllipsis(
                      selectedCandidate.position,
                      windowWidth < 1570 ? 50 : 70,
                    )}
                  </p>
                </div>
              </div>
              <div className="candidate-details-inner-container">
                <h3 className="schedule-header">Interview Details</h3>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={interviewPersonIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>{isHiringTeamTenant ? "Coordinator" : "Interviewer"}</h4>
                  </div>
                  <p>
                    {formatInterviewerName(selectedCandidate.interviewPerson)}
                  </p>
                </div>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={dateIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>When</h4>
                  </div>
                  <p>
                    {`${selectedCandidate.date} - ${selectedCandidate.startTime} To
                  ${selectedCandidate.endTime} (${selectedCandidate.timezone})`}
                  </p>
                </div>
                <div className="candidate-detail">
                  <div className="icon-with-header">
                    <img
                      src={dateIcon}
                      alt="candidateIcon"
                      className="candidate-detail-icon"
                    />
                    <h4>Interview Type</h4>
                  </div>
                  <p style={{ textTransform: "capitalize" }}>
                    {selectedCandidate.interviewType}
                  </p>
                </div>
                {selectedCandidate.meetingLink &&
                  selectedCandidate.interviewType === "online" && (
                    <div className="candidate-detail">
                      <div className="icon-with-header">
                        <img
                          src={linkIcon}
                          alt="candidateIcon"
                          className="candidate-detail-icon"
                        />
                        <h4>Meeting Link</h4>
                      </div>
                      <div className="meeting-link-scroll-container">
                        <a
                          href={selectedCandidate.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="detail-value link"
                        >
                          {selectedCandidate.meetingLink}
                        </a>
                      </div>
                    </div>
                  )}
              </div>
              {storePanelData.length > 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    background:
                      "var(--sidepopup-content-background, transparent)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      flexWrap: "wrap",
                      marginBottom: "18px",
                    }}
                  >
                    <h3
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        margin: 0,
                        color: "var(--primary__color)",
                        fontSize: "var(--candidate-interview-font-16, 1rem)",
                        fontWeight: 500,
                      }}
                    >
                      Interview Panel &amp; Evaluators ({storePanelData.length})
                    </h3>
                    <span
                      style={{
                        color: "var(--card__sub_Header_color)",
                        fontSize: "var(--candidate-interview-font-14, .875rem)",
                      }}
                    >
                      Assigned Evaluators
                    </span>
                  </div>

                  {storePanelData.map((evaluator) => (
                    <div
                      key={evaluator.user_name || evaluator.user_id}
                      style={{
                        border: "1px solid var(--border__color, #e7e8ef)",
                        borderRadius: "20px",
                        padding: "20px 24px 16px",
                        marginBottom: "16px",
                        background:
                          "var(--sidepopup-card-background, transparent)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "20px",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "50%",
                              border: "2px solid var(--border__color)",
                              background: "var(--skills_chips_background)",
                              color: "var(--primary__color)",
                              fontSize:
                                "var(--candidate-interview-font-20, 1.25rem)",
                              fontWeight: 500,
                            }}
                          >
                            {(evaluator.first_name || "N").slice(0, 2)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                color: "var(--header__color)",
                                fontSize:
                                  "var(--candidate-interview-font-16, 1rem)",
                                fontWeight: 500,
                                marginBottom: "6px",
                              }}
                            >
                              {evaluator.first_name} {evaluator.last_name}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          borderTop: "1px solid var(--border__color, #ececf2)",
                          marginTop: "18px",
                          paddingTop: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "16px",
                        }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          {evaluator.rating ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <div
                                    aria-label={`Evaluation score ${evaluator.rating} out of 5`}
                                    style={{
                                      display: "flex",
                                      gap: "2px",
                                      color: "var(--chip-border-shortlisted)",
                                      fontSize:
                                        "var(--candidate-interview-font-22, 1.375rem)",
                                      lineHeight: 1.1,
                                      letterSpacing: "-1px",
                                      marginBottom: "7px",
                                    }}
                                  >
                                    {Array.from({ length: 5 }, (_, index) => (
                                      <span
                                        key={index}
                                        style={{
                                          color:
                                            index < evaluator.rating
                                              ? "var(--chip-border-shortlisted)"
                                              : "var(--border__color)",
                                        }}
                                      >
                                        ★
                                      </span>
                                    ))}
                                  </div>
                                  {evaluator?.notes && (
                                    <p
                                      style={{
                                        margin: 0,
                                        color: "var(--card__sub_Header_color)",
                                        fontSize:
                                          "var(--candidate-interview-font-14, .875rem)",
                                        fontStyle: "italic",
                                        lineHeight: 1.5,
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      "{evaluator.notes}"
                                    </p>
                                  )}
                                </div>
                                <span>{evaluator.hiring_recommendation}</span>
                              </div>
                            </>
                          ) : (
                            <span
                              style={{
                                color: "var(--card__sub_Header_color)",
                                fontSize:
                                  "var(--candidate-interview-font-14, .875rem)",
                                fontStyle: "italic",
                              }}
                            >
                              Feedback pending evaluation
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="interview-reschedule-button-container">
                <Button
                  label="Reschedule Interview"
                  isCancel={true}
                  iconSrc={rescheduleIcon}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    marginRight: 0,
                  }}
                  event={handleRescheduleInterview}
                />
                <Button
                  label="Cancel Interview"
                  isCancel={true}
                  iconSrc={cancelScheduleIcon}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    margin: 0,
                  }}
                  event={handleOpenDeleteModal}
                />
              </div>
            </div>
          </div>
        )}
      </SidePopup>
      {isDatePickerOpen &&
        pickerPosition.top !== null &&
        ReactDOM.createPortal(
          <CustomDatePickerModal
            initialDate={
              activeDatePicker === "interview"
                ? scheduleformik.values.interviewDate
                  ? new Date(scheduleformik.values.interviewDate)
                  : new Date()
                : selectedDate
            }
            onOk={(date) => {
              if (activeDatePicker === "interview") {
                scheduleformik.setFieldValue("interviewDate", date);
                scheduleformik.setFieldTouched("interviewDate", true);
                scheduleformik.validateField("interviewDate");
              } else {
                setSelectedDate(date);
              }
              setIsDatePickerOpen(false);
              setActiveDatePicker(null);
              setPickerPosition({ top: null, left: null });
            }}
            onClose={() => {
              setIsDatePickerOpen(false);
              if (activeDatePicker === "interview") {
                scheduleformik.setFieldTouched("interviewDate", true);
              }
              setActiveDatePicker(null);
              setPickerPosition({ top: null, left: null });
            }}
            top={pickerPosition.top}
            left={pickerPosition.left}
            ref={pickerRef}
          />,
          document.body,
        )}
      {ReactDOM.createPortal(
        <StandardPopup
          isOpen={isFeedbackPopupOpen}
          onClose={() => {
            setIsFeedbackPopupOpen(false);
            feedbackFormik.resetForm();
          }}
          title="Record Interviewer Feedback"
          hideActions={true}
          width="640px"
          maxHeight="90vh"
          popupStyle={{
            borderRadius: "24px",
            padding: 0,
            overflow: "hidden",
          }}
          contentStyle={{
            padding: 0,
            marginTop: 0,
          }}
        >
          <form
            onSubmit={feedbackFormik.handleSubmit}
            style={{
              boxSizing: "border-box",
              padding: "0 34px 32px",
              color: "var(--header__color)",
            }}
          >
            <section style={{ marginBottom: "30px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  marginTop: "30px",
                  color: "var(--header__color)",
                  fontSize: "var(--candidate-interview-font-16, 1rem)",
                  fontWeight: 500,
                }}
              >
                Evaluation Score ({feedbackFormik.values.score} to 5 Stars)
              </label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "26px" }}
              >
                <div
                  role="group"
                  aria-label="Evaluation score"
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isSelected =
                      star <=
                      (hoveredFeedbackScore || feedbackFormik.values.score);
                    return (
                      <StyledButton
                        type="button"
                        key={star}
                        aria-label={`${star} ${star === 1 ? "star" : "stars"}`}
                        aria-pressed={feedbackFormik.values.score === star}
                        onMouseEnter={() => setHoveredFeedbackScore(star)}
                        onMouseLeave={() => setHoveredFeedbackScore(0)}
                        onFocus={() => setHoveredFeedbackScore(star)}
                        onBlur={() => setHoveredFeedbackScore(0)}
                        onClick={() =>
                          feedbackFormik.setFieldValue("score", star)
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "38px",
                          height: "38px",
                          padding: 0,
                          border: 0,
                          background: "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={
                            isSelected ? SelectedStarIcon : NotSelectedStarIcon
                          }
                          alt=""
                          style={{ width: "29px", height: "29px" }}
                        />
                      </StyledButton>
                    );
                  })}
                </div>
                <span
                  style={{
                    whiteSpace: "nowrap",
                    color: "var(--header__color)",
                    fontSize: "var(--candidate-interview-font-16, 1rem)",
                  }}
                >
                  {feedbackFormik.values.score} / 5
                </span>
              </div>
            </section>

            <section style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  color: "var(--header__color)",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Recommendation
              </label>
              <MonthFilter
                value={feedbackFormik.values.hiringRecommendation}
                onChange={(value) =>
                  feedbackFormik.setFieldValue("hiringRecommendation", value)
                }
                options={[
                  { label: "Strong Hire", value: "Strong Hire" },
                  { label: "Hire", value: "Hire" },
                  { label: "Hold", value: "Hold" },
                  { label: "No Hire", value: "No Hire" },
                ]}
                ariaLabel="Hiring recommendation"
              />
            </section>

            <div>
              <TextArea
                name="feedbackNotes"
                label="Feedback Notes & Technical Justification"
                placeholder="Candidate demonstrated deep technical competence, handled edge cases well..."
                value={feedbackFormik.values.feedbackNotes}
                onChange={feedbackFormik.handleChange}
                onBlur={feedbackFormik.handleBlur}
                rows={4}
                containerClassName="input-field"
                containerStyle={{ width: "100%" }}
                style={{
                  height: "100px",
                  border: "1px solid var(--border__color)",
                  borderRadius: "12px",
                  padding: "16px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  fontSize: "var(--candidate-interview-font-15, .9375rem)",
                  lineHeight: 1.5,
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "36px",
              }}
            >
              <StyledButton
                type="button"
                onClick={() => setIsFeedbackPopupOpen(false)}
                style={{
                  minHeight: "46px",
                  padding: "0 18px",
                  border: "1px solid var(--border__color)",
                  borderRadius: "14px",
                  background: "transparent",
                  color: "var(--header__color)",
                  fontFamily: "inherit",
                  fontSize: "var(--candidate-interview-font-15, .9375rem)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </StyledButton>
              <StyledButton
                type="submit"
                style={{
                  minHeight: "46px",
                  padding: "0 18px",
                  border: "1px solid var(--primary__color)",
                  borderRadius: "14px",
                  background: "var(--primary__color)",
                  color: "#fff",
                  fontFamily: "inherit",
                  fontSize: "var(--candidate-interview-font-15, .9375rem)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Save Evaluation
              </StyledButton>
            </div>
          </form>
        </StandardPopup>,
        document.body,
      )}
      {ReactDOM.createPortal(
        <StandardPopup
          isOpen={isEmailPreviewOpen}
          onClose={() => setIsEmailPreviewOpen(false)}
          title="Email Preview"
          hideActions={true}
          width="900px"
          maxHeight="95vh"
          popupStyle={{
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          contentStyle={{
            padding: 0,
            marginTop: 0,
          }}
        >
          <div style={{ overflow: "hidden" }}>
            <EmailPreviewPopup htmlContent={previewData.body} />
          </div>
        </StandardPopup>,
        document.body,
      )}
      <ContentDialog
        isOpen={showDeleteModal}
        title="Delete Interview"
        onClose={handleCloseDeleteModal}
        submitButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        event={handleDeleteEvent}
        showCloseIcon={false}
        contentHeight="auto"
      >
        <div
          style={{
            padding: "15px 0",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "rgba(255, 82, 82, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <img
              src={cancelScheduleIcon}
              alt="Cancel"
              style={{
                width: "30px",
                height: "30px",
                opacity: 0.8,
              }}
            />
          </div>
          <p
            style={{
              fontSize: "var(--candidate-interview-font-16, 1rem)",
              lineHeight: "1.5",
              color: "var(--header__color)",
            }}
          >
            Are you sure you want to delete this interview with
            <span
              style={{
                fontWeight: "600",
                color: "var(--primary__color)",
                margin: "0 5px",
              }}
            >
              {selectedCandidate?.name}
            </span>
            ?
          </p>
          <p
            style={{
              fontSize: "var(--candidate-interview-font-14, .875rem)",
              color: "var(--card__sub_Header_color)",
              marginTop: "10px",
            }}
          >
            This action cannot be undone.
          </p>
        </div>
      </ContentDialog>
    </div>
  );
};

export default CandidateInterviewScheduler;